import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Zap, Plus, Edit2, Trash2, Play, XCircle, RotateCcw, ChevronRight,
    X, Check, Clock, CheckCircle, AlertTriangle, Ban, Terminal, Mail, Globe, Eye,
    Copy, Sparkles, WifiOff, Wifi,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { getTicketTypeConfig } from "../../lib/utils";
import { toast } from "sonner";

const TRIGGER_EVENTS = [
    { value: "ticket_approved", label: "Ticket approuvé" },
    { value: "ticket_resolved", label: "Ticket résolu" },
    { value: "ticket_created", label: "Ticket créé" },
];

const ACTION_TYPES = [
    { value: "email", label: "Email", icon: Mail },
    { value: "webhook", label: "Webhook", icon: Globe },
    { value: "script", label: "Script", icon: Terminal },
];

const TICKET_TYPES = [
    "incident", "problem", "change", "service_request", "access_request",
    "software_install", "software_update", "new_equipment", "onboarding", "offboarding",
    "security", "question", "other", "password_reset", "defective_equipment", "equipment_loan",
];

const STATUS_CONFIG = {
    pending: { label: "En attente", color: "bg-amber-100 text-amber-700", icon: Clock },
    running: { label: "En cours", color: "bg-blue-100 text-blue-700", icon: Play },
    success: { label: "Succès", color: "bg-green-100 text-green-700", icon: CheckCircle },
    failed: { label: "Échoué", color: "bg-red-100 text-red-700", icon: AlertTriangle },
    cancelled: { label: "Annulé", color: "bg-gray-100 text-gray-500", icon: Ban },
};

function JobStatusBadge({ status }) {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = cfg.icon;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full ${cfg.color}`} data-testid={`job-status-${status}`}>
            <Icon className="w-3 h-3" />
            {cfg.label}
        </span>
    );
}

function ActionTypeBadge({ type }) {
    const cfg = ACTION_TYPES.find((a) => a.value === type) || ACTION_TYPES[0];
    const Icon = cfg.icon;
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 text-gray-600">
            <Icon className="w-3 h-3" />
            {cfg.label}
        </span>
    );
}

// ==================== RULE EDITOR ====================

function RuleEditor({ rule, onSave, onCancel }) {
    const [name, setName] = useState(rule?.name || "");
    const [triggerEvent, setTriggerEvent] = useState(rule?.trigger_event || "ticket_approved");
    const [actionType, setActionType] = useState(rule?.action_type || "email");
    const [isActive, setIsActive] = useState(rule?.is_active !== false);
    const [typeFilter, setTypeFilter] = useState(rule?.ticket_type_filter || []);
    const [saving, setSaving] = useState(false);

    // Action config states
    const [emailTo, setEmailTo] = useState(rule?.action_config?.to || "");
    const [emailSubject, setEmailSubject] = useState(rule?.action_config?.subject || "");
    const [emailBody, setEmailBody] = useState(rule?.action_config?.body_template || "");
    const [webhookUrl, setWebhookUrl] = useState(rule?.action_config?.url || "");
    const [webhookHeaders, setWebhookHeaders] = useState(rule?.action_config?.headers ? JSON.stringify(rule.action_config.headers, null, 2) : "{}");
    const [scriptContent, setScriptContent] = useState(rule?.action_config?.script_content || "");
    const [scriptTimeout, setScriptTimeout] = useState(rule?.action_config?.timeout_seconds || 30);
    const [scriptDir, setScriptDir] = useState(rule?.action_config?.working_directory || "/tmp/automation");

    const toggleType = (t) => {
        setTypeFilter((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
    };

    const buildActionConfig = () => {
        if (actionType === "email") {
            return { to: emailTo, subject: emailSubject, body_template: emailBody };
        }
        if (actionType === "webhook") {
            let headers = {};
            try { headers = JSON.parse(webhookHeaders); } catch {}
            return { url: webhookUrl, headers, timeout_seconds: 30 };
        }
        if (actionType === "script") {
            return { script_content: scriptContent, timeout_seconds: scriptTimeout, working_directory: scriptDir };
        }
        return {};
    };

    const handleSave = async () => {
        if (!name.trim()) { toast.error("Le nom est requis"); return; }
        setSaving(true);
        try {
            await onSave({
                name: name.trim(),
                trigger_event: triggerEvent,
                ticket_type_filter: typeFilter,
                conditions: {},
                action_type: actionType,
                action_config: buildActionConfig(),
                is_active: isActive,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="rule-editor">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">{rule ? "Modifier la règle" : "Nouvelle règle"}</h2>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={onCancel}>Annuler</Button>
                    <Button size="sm" onClick={handleSave} disabled={saving || !name.trim()} data-testid="save-rule-btn">
                        <Check className="w-3.5 h-3.5 mr-1" />
                        {saving ? "..." : "Enregistrer"}
                    </Button>
                </div>
            </div>

            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom de la règle" className="text-base font-medium" data-testid="rule-name-input" />

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Déclencheur</label>
                    <Select value={triggerEvent} onValueChange={setTriggerEvent}>
                        <SelectTrigger data-testid="rule-trigger-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {TRIGGER_EVENTS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Type d'action</label>
                    <Select value={actionType} onValueChange={setActionType}>
                        <SelectTrigger data-testid="rule-action-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {ACTION_TYPES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Active toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="rounded" data-testid="rule-active-checkbox" />
                <span className="text-sm text-gray-700">Règle active</span>
            </label>

            {/* Type filter */}
            <div>
                <p className="text-xs font-medium text-gray-500 mb-1.5">Filtrer par type de ticket (vide = tous)</p>
                <div className="flex flex-wrap gap-1.5">
                    {TICKET_TYPES.map((t) => {
                        const cfg = getTicketTypeConfig(t);
                        const selected = typeFilter.includes(t);
                        return (
                            <button key={t} onClick={() => toggleType(t)}
                                className={`px-2 py-0.5 text-[10px] font-medium rounded-full border transition-colors ${selected ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-white text-gray-400 border-gray-200 hover:border-gray-300"}`}
                                data-testid={`rule-type-${t}`}
                            >
                                {cfg.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Action config */}
            <div className="border rounded-lg p-4 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Configuration de l'action</h3>
                {actionType === "email" && (
                    <div className="space-y-3">
                        <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="Destinataire (ex: achats@entreprise.ch ou {{requester_id}})" data-testid="email-to-input" />
                        <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="Sujet (ex: Ticket {{ticket_number}} approuvé)" data-testid="email-subject-input" />
                        <Textarea value={emailBody} onChange={(e) => setEmailBody(e.target.value)} placeholder="Corps du mail (variables: {{ticket_number}}, {{title}}, {{type}}, {{status}}, {{priority}})" className="min-h-[120px] font-mono text-sm" data-testid="email-body-input" />
                    </div>
                )}
                {actionType === "webhook" && (
                    <div className="space-y-3">
                        <Input value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder="URL du webhook (https://...)" data-testid="webhook-url-input" />
                        <Textarea value={webhookHeaders} onChange={(e) => setWebhookHeaders(e.target.value)} placeholder='Headers JSON (ex: {"Authorization": "Bearer ..."})'  className="min-h-[80px] font-mono text-xs" data-testid="webhook-headers-input" />
                    </div>
                )}
                {actionType === "script" && (
                    <div className="space-y-3">
                        <div className="p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                            <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                            Les scripts s'exécutent sur le serveur. Réservé aux administrateurs. Répertoires autorisés : /tmp/automation, /app/scripts
                        </div>
                        <Textarea value={scriptContent} onChange={(e) => setScriptContent(e.target.value)} placeholder="#!/bin/bash&#10;echo 'Hello from automation'" className="min-h-[160px] font-mono text-sm" data-testid="script-content-input" />
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">Timeout (secondes, max 300)</label>
                                <Input type="number" value={scriptTimeout} onChange={(e) => setScriptTimeout(Number(e.target.value))} min={1} max={300} data-testid="script-timeout-input" />
                            </div>
                            <div>
                                <label className="text-xs text-gray-500 mb-1 block">Répertoire de travail</label>
                                <Input value={scriptDir} onChange={(e) => setScriptDir(e.target.value)} data-testid="script-dir-input" />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ==================== JOB DETAIL PANEL ====================

function JobDetailPanel({ job, onClose, onRetry, onCancel }) {
    if (!job) return null;
    const canRetry = job.status === "failed" && job.attempts < (job.max_attempts || 3);
    const canCancel = job.status === "pending" || job.status === "failed";

    return (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="job-detail-panel">
            <div className="absolute inset-0 bg-black/20" onClick={onClose} />
            <div className="relative w-full max-w-lg bg-white shadow-xl border-l flex flex-col">
                <div className="flex items-center justify-between px-5 py-4 border-b">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900">Détail du job</h3>
                        <p className="text-[10px] text-gray-400 font-mono mt-0.5">{job.id}</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div><span className="text-[10px] text-gray-400 block">Statut</span><JobStatusBadge status={job.status} /></div>
                        <div><span className="text-[10px] text-gray-400 block">Action</span><ActionTypeBadge type={job.action_type} /></div>
                        <div><span className="text-[10px] text-gray-400 block">Ticket</span><span className="text-xs font-mono">{job.ticket_id?.slice(0, 12)}</span></div>
                        <div><span className="text-[10px] text-gray-400 block">Événement</span><span className="text-xs">{TRIGGER_EVENTS.find(e => e.value === job.trigger_event)?.label || job.trigger_event}</span></div>
                        <div><span className="text-[10px] text-gray-400 block">Tentatives</span><span className="text-xs">{job.attempts} / {job.max_attempts || 3}</span></div>
                        <div><span className="text-[10px] text-gray-400 block">Créé par</span><span className="text-xs">{job.created_by}</span></div>
                    </div>

                    {/* Timestamps */}
                    <div className="space-y-1 text-xs text-gray-500">
                        {job.scheduled_at && <div>Planifié : {new Date(job.scheduled_at).toLocaleString("fr-CH")}</div>}
                        {job.started_at && <div>Démarré : {new Date(job.started_at).toLocaleString("fr-CH")}</div>}
                        {job.completed_at && <div>Terminé : {new Date(job.completed_at).toLocaleString("fr-CH")}</div>}
                    </div>

                    {/* Output */}
                    {job.output && (
                        <div>
                            <span className="text-[10px] text-gray-400 block mb-1">Sortie</span>
                            <pre className="p-3 bg-gray-900 text-green-400 text-xs font-mono rounded-lg overflow-x-auto max-h-60 whitespace-pre-wrap" data-testid="job-output">{job.output}</pre>
                        </div>
                    )}

                    {/* Error */}
                    {job.error && (
                        <div>
                            <span className="text-[10px] text-red-400 block mb-1">Erreur</span>
                            <pre className="p-3 bg-red-50 text-red-700 text-xs font-mono rounded-lg overflow-x-auto max-h-40 whitespace-pre-wrap" data-testid="job-error">{job.error}</pre>
                        </div>
                    )}

                    {/* Action config */}
                    <div>
                        <span className="text-[10px] text-gray-400 block mb-1">Configuration</span>
                        <pre className="p-3 bg-gray-50 text-gray-700 text-xs font-mono rounded-lg overflow-x-auto max-h-40 whitespace-pre-wrap">{JSON.stringify(job.action_config, null, 2)}</pre>
                    </div>
                </div>

                {/* Actions */}
                <div className="px-5 py-3 border-t flex items-center gap-2">
                    {canRetry && (
                        <Button size="sm" variant="outline" onClick={() => onRetry(job.id)} data-testid="retry-job-btn">
                            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Relancer
                        </Button>
                    )}
                    {canCancel && (
                        <Button size="sm" variant="outline" onClick={() => onCancel(job.id)} data-testid="cancel-job-btn" className="text-red-600 hover:text-red-700">
                            <Ban className="w-3.5 h-3.5 mr-1" /> Annuler
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ==================== MAIN COMPONENT ====================

export default function AdminAutomation() {
    const { currentUser } = useApp();
    const [tab, setTab] = useState("rules");

    // Rules state
    const [rules, setRules] = useState([]);
    const [rulesLoading, setRulesLoading] = useState(true);
    const [editingRule, setEditingRule] = useState(null);

    // Templates state
    const [templates, setTemplates] = useState([]);
    const [smtpConfigured, setSmtpConfigured] = useState(null);

    // Jobs state
    const [jobs, setJobs] = useState([]);
    const [jobsLoading, setJobsLoading] = useState(true);
    const [jobFilter, setJobFilter] = useState("all");
    const [selectedJob, setSelectedJob] = useState(null);
    const pollingRef = useRef(null);

    // Load rules
    const loadRules = useCallback(async () => {
        try {
            setRulesLoading(true);
            const data = await api.getAutomationRules();
            setRules(data);
        } catch { toast.error("Erreur chargement des règles"); }
        finally { setRulesLoading(false); }
    }, []);

    // Load templates + SMTP status
    useEffect(() => {
        api.getAutomationTemplates().then(setTemplates).catch(() => {});
        api.getSmtpStatus().then((d) => setSmtpConfigured(d.configured)).catch(() => {});
    }, []);

    // Load jobs
    const loadJobs = useCallback(async () => {
        try {
            const params = {};
            if (jobFilter !== "all") params.status = jobFilter;
            const data = await api.getAutomationJobs(params);
            setJobs(data);
        } catch { /* silent for polling */ }
        finally { setJobsLoading(false); }
    }, [jobFilter]);

    useEffect(() => { loadRules(); }, [loadRules]);
    useEffect(() => { loadJobs(); }, [loadJobs]);

    // Polling for jobs tab
    useEffect(() => {
        if (tab === "jobs") {
            pollingRef.current = setInterval(loadJobs, 5000);
            return () => clearInterval(pollingRef.current);
        }
        return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
    }, [tab, loadJobs]);

    // Rule CRUD
    const handleSaveRule = async (data) => {
        try {
            if (editingRule === "new") {
                await api.createAutomationRule({ ...data, created_by: currentUser.id });
                toast.success("Règle créée");
            } else {
                await api.updateAutomationRule(editingRule.id, data);
                toast.success("Règle mise à jour");
            }
            setEditingRule(null);
            loadRules();
        } catch { toast.error("Erreur lors de l'enregistrement"); }
    };

    const handleDeleteRule = async (id) => {
        if (!window.confirm("Supprimer cette règle ?")) return;
        try {
            await api.deleteAutomationRule(id);
            toast.success("Règle supprimée");
            loadRules();
        } catch { toast.error("Erreur lors de la suppression"); }
    };

    const handleActivateTemplate = async (tpl) => {
        try {
            await api.activateAutomationTemplate(tpl.id, { created_by: currentUser.id });
            toast.success(`Règle « ${tpl.name} » activée`);
            loadRules();
        } catch { toast.error("Erreur lors de l'activation"); }
    };

    const handleRetryJob = async (id) => {
        try {
            await api.retryAutomationJob(id);
            toast.success("Job relancé");
            loadJobs();
            if (selectedJob?.id === id) {
                const updated = await api.getAutomationJob(id);
                setSelectedJob(updated);
            }
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleCancelJob = async (id) => {
        try {
            await api.cancelAutomationJob(id);
            toast.success("Job annulé");
            loadJobs();
            if (selectedJob?.id === id) {
                const updated = await api.getAutomationJob(id);
                setSelectedJob(updated);
            }
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const openJobDetail = async (job) => {
        try {
            const full = await api.getAutomationJob(job.id);
            setSelectedJob(full);
        } catch { toast.error("Erreur chargement du job"); }
    };

    // Rule editor
    if (editingRule) {
        return (
            <div className="max-w-3xl">
                <RuleEditor
                    rule={editingRule === "new" ? null : editingRule}
                    onSave={handleSaveRule}
                    onCancel={() => setEditingRule(null)}
                />
            </div>
        );
    }

    return (
        <div data-testid="admin-automation-page">
            <div className="page-header flex items-center justify-between mb-4">
                <div>
                    <h1 className="page-title flex items-center gap-2">
                        <Zap className="w-6 h-6" />
                        Automatisation
                    </h1>
                    <p className="page-subtitle">{rules.length} règle{rules.length !== 1 ? "s" : ""} &middot; {jobs.length} exécution{jobs.length !== 1 ? "s" : ""}</p>
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="mb-4" data-testid="automation-tabs">
                    <TabsTrigger value="rules" data-testid="tab-rules">Règles</TabsTrigger>
                    <TabsTrigger value="jobs" data-testid="tab-jobs">
                        Exécutions
                        {jobs.some((j) => j.status === "failed") && (
                            <span className="ml-1.5 px-1.5 py-0.5 text-[9px] font-bold bg-red-500 text-white rounded-full">
                                {jobs.filter((j) => j.status === "failed").length}
                            </span>
                        )}
                    </TabsTrigger>
                </TabsList>

                {/* ==================== RULES TAB ==================== */}
                <TabsContent value="rules">
                    {/* SMTP Status banner */}
                    {smtpConfigured !== null && (
                        <div className={`mb-4 p-3 rounded-lg border text-xs flex items-center gap-2 ${smtpConfigured ? "bg-green-50 border-green-200 text-green-700" : "bg-amber-50 border-amber-200 text-amber-700"}`} data-testid="smtp-status-banner">
                            {smtpConfigured ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
                            {smtpConfigured
                                ? "SMTP configuré — les emails sont envoyés en réel."
                                : "SMTP non configuré — les emails sont simulés (logs uniquement). Configurez SMTP_HOST, SMTP_PORT, SMTP_USER, SMTP_PASSWORD dans le .env backend."
                            }
                        </div>
                    )}

                    {/* Templates section */}
                    {templates.length > 0 && (
                        <div className="mb-6" data-testid="templates-section">
                            <div className="flex items-center gap-2 mb-3">
                                <Sparkles className="w-4 h-4 text-amber-500" />
                                <h3 className="text-sm font-semibold text-gray-700">Templates prêts à l'emploi</h3>
                            </div>
                            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                {templates.map((tpl) => {
                                    const alreadyActive = rules.some((r) => r.template_id === tpl.id);
                                    return (
                                        <div key={tpl.id} className="card group" data-testid={`template-${tpl.id}`}>
                                            <div className="card-body">
                                                <div className="flex items-start justify-between mb-1.5">
                                                    <ActionTypeBadge type={tpl.action_type} />
                                                    {alreadyActive && (
                                                        <span className="text-[9px] font-medium text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">Actif</span>
                                                    )}
                                                </div>
                                                <h4 className="text-xs font-semibold text-gray-900 mb-1">{tpl.name}</h4>
                                                <p className="text-[10px] text-gray-500 mb-2 line-clamp-2">{tpl.description}</p>
                                                <div className="flex items-center gap-2 text-[10px] text-gray-400 mb-2">
                                                    <span>{TRIGGER_EVENTS.find((e) => e.value === tpl.trigger_event)?.label}</span>
                                                    {(tpl.ticket_type_filter || []).length > 0 && (
                                                        <span>{tpl.ticket_type_filter.map((t) => getTicketTypeConfig(t).label).join(", ")}</span>
                                                    )}
                                                </div>
                                                <button
                                                    onClick={() => handleActivateTemplate(tpl)}
                                                    disabled={alreadyActive}
                                                    className={`w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${alreadyActive ? "bg-gray-100 text-gray-400 cursor-default" : "bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"}`}
                                                    data-testid={`activate-template-${tpl.id}`}
                                                >
                                                    {alreadyActive ? <><Check className="w-3 h-3" /> Déjà activé</> : <><Copy className="w-3 h-3" /> Activer</>}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Rules list */}
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-700">Règles actives</h3>
                        <Button onClick={() => setEditingRule("new")} data-testid="new-rule-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Nouvelle règle
                        </Button>
                    </div>

                    {rulesLoading ? (
                        <div className="text-center py-12 text-gray-400">Chargement...</div>
                    ) : rules.length === 0 ? (
                        <div className="text-center py-12">
                            <Zap className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">Aucune règle d'automatisation</p>
                            <p className="text-xs text-gray-400 mt-1">Créez une règle pour automatiser vos workflows</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {rules.map((rule) => (
                                <div key={rule.id} className="card hover:border-gray-300 transition-colors" data-testid={`rule-${rule.id}`}>
                                    <div className="card-body flex items-center gap-4">
                                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${rule.is_active ? "bg-green-500" : "bg-gray-300"}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <h3 className="text-sm font-semibold text-gray-900 truncate">{rule.name}</h3>
                                                <ActionTypeBadge type={rule.action_type} />
                                            </div>
                                            <div className="flex items-center gap-3 text-[10px] text-gray-400">
                                                <span>{TRIGGER_EVENTS.find((e) => e.value === rule.trigger_event)?.label || rule.trigger_event}</span>
                                                {(rule.ticket_type_filter || []).length > 0 && (
                                                    <span>{rule.ticket_type_filter.map((t) => getTicketTypeConfig(t).label).join(", ")}</span>
                                                )}
                                                {!rule.is_active && <span className="text-amber-500 font-medium">Inactive</span>}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => setEditingRule(rule)} className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors" title="Modifier">
                                                <Edit2 className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => handleDeleteRule(rule.id)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors" title="Supprimer" data-testid={`delete-rule-${rule.id}`}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>

                {/* ==================== JOBS TAB ==================== */}
                <TabsContent value="jobs">
                    <div className="flex items-center gap-2 mb-3">
                        {["all", "pending", "running", "success", "failed", "cancelled"].map((s) => (
                            <button
                                key={s}
                                onClick={() => setJobFilter(s)}
                                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${jobFilter === s ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-100"}`}
                                data-testid={`job-filter-${s}`}
                            >
                                {s === "all" ? "Tous" : STATUS_CONFIG[s]?.label || s}
                            </button>
                        ))}
                    </div>

                    {jobsLoading ? (
                        <div className="text-center py-12 text-gray-400">Chargement...</div>
                    ) : jobs.length === 0 ? (
                        <div className="text-center py-12">
                            <Terminal className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">Aucune exécution</p>
                        </div>
                    ) : (
                        <div className="space-y-1.5">
                            {jobs.map((job) => (
                                <button
                                    key={job.id}
                                    onClick={() => openJobDetail(job)}
                                    className="w-full card text-left hover:border-gray-300 transition-all group"
                                    data-testid={`job-${job.id}`}
                                >
                                    <div className="card-body flex items-center gap-3 py-2.5">
                                        <JobStatusBadge status={job.status} />
                                        <ActionTypeBadge type={job.action_type} />
                                        <div className="flex-1 min-w-0">
                                            <span className="text-xs text-gray-600 font-mono">{job.ticket_id?.slice(0, 12)}</span>
                                            {job.ticket_type && (
                                                <span className="ml-2 text-[10px] text-gray-400">{getTicketTypeConfig(job.ticket_type).label}</span>
                                            )}
                                        </div>
                                        <span className="text-[10px] text-gray-400">
                                            {job.scheduled_at ? new Date(job.scheduled_at).toLocaleString("fr-CH", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}
                                        </span>
                                        <span className="text-[10px] text-gray-300">
                                            {job.attempts}/{job.max_attempts || 3}
                                        </span>
                                        <ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500" />
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* Job detail slide-over */}
            {selectedJob && (
                <JobDetailPanel
                    job={selectedJob}
                    onClose={() => setSelectedJob(null)}
                    onRetry={handleRetryJob}
                    onCancel={handleCancelJob}
                />
            )}
        </div>
    );
}
